import { motion } from "framer-motion";
import { Linkedin, Instagram, ArrowUpRight } from "lucide-react";

const FOUNDERS = [
  {
    name: "Mukund Naair",
    role: "CEO & Co-Founder",
    bio: "A business leader with 25+ years of experience across operations, supply chain and business management. Mukund brings strong leadership, execution and customer focus to Fitygrity.",
    link: "https://www.instagram.com/mukundnaair",
    social: "instagram",
    photo: "/founders/mukund-naair.png",
  },
  {
    name: "Mihika Wahi Gupta",
    role: "CBO & Co-Founder",
    bio: "An endurance athlete, fitness entrepreneur and wellness advocate, Mihika brings her experience in fitness, community building and brand development to Fitygrity.",
    link: "https://www.linkedin.com/in/mihika-wahi-gupta-6a87081a3",
    social: "linkedin",
    photo: "/founders/mihika-wahi-gupta.jpg",
  },
  {
    name: "Gaurav Pant",
    role: "Advisor & Coach",
    bio: "A senior business and governance leader with extensive experience in financial services and global organisations. An endurance athlete and running coach, Gaurav brings strategic and governance expertise to Fitygrity.",
    link: "https://www.linkedin.com/in/gauravpant",
    social: "linkedin",
    photo: "/founders/gaurav-pant.png",
  },
  {
    name: "Meenakshi Meghani",
    role: "Independent Director",
    bio: "A seeker at heart, a guide by practice. Deeply rooted in yoga, meditation and spirituality, she believes in healing from within. Her journey is about helping others find stillness in chaos, strength in softness, and divinity within themselves.",
    link: "https://www.instagram.com/meenakshinmeghani/",
    social: "instagram",
    photo: "/founders/meenakshi-meghani.jpg",
  },
  {
    name: "Dr. Ramit Wadhwa",
    role: "Independent Director | Cardiologist",
    bio: "A cardiologist with a strong interest in preventive health, fitness and wellbeing. Dr. Ramit brings valuable medical and cardiovascular expertise to Fitygrity, strengthening its focus on safe, healthy and sustainable performance.",
    link: "https://www.linkedin.com/in/dr-ramit-wadhwa-72633938",
    social: "linkedin",
    photo: "/founders/ramit-wadhwa.jpeg",
  },
];

export default function Founders() {
  return (
    <section id="founders" className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40 border-t border-[#0D1117]/10 bg-[#F4F2EC]">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="mb-14 sm:mb-20"
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5">Our Leadership</p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#0D1117] tracking-tight max-w-2xl">
          Five minds. One obsession with the edge of human stamina.
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {FOUNDERS.map((f, i) => (
          <motion.div
            key={f.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
            data-testid="founder-card"
            className={`group rounded-2xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] overflow-hidden transition-colors duration-500 ${
              i === 4 ? "sm:col-span-2 lg:col-span-1" : ""
            }`}
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              {f.photo ? (
                <img
                  src={f.photo}
                  alt={`Portrait of ${f.name}`}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-700 ease-out"
                />
              ) : (
                <div className="w-full h-full bg-[#F4F2EC] flex items-center justify-center" aria-label={`Portrait of ${f.name} coming soon`}>
                  <span className="font-serif text-7xl sm:text-8xl font-light text-stroke-amber select-none">
                    {f.name.split(" ").map((w) => w[0]).join("")}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] via-transparent to-transparent" />
              {f.link && (
                <a
                  href={f.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${f.name} on ${f.social === "linkedin" ? "LinkedIn" : "Instagram"}`}
                  data-testid="founder-social-link"
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#FBF9F5]/70 backdrop-blur border border-[#0D1117]/15 flex items-center justify-center text-[#0D1117] hover:bg-[#FF4E00] hover:text-[#FBF9F5] hover:border-[#FF4E00] transition-all duration-300"
                >
                  {f.social === "linkedin" ? <Linkedin className="w-4 h-4" /> : <Instagram className="w-4 h-4" />}
                </a>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-serif text-xl sm:text-2xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors duration-300">
                  {f.name}
                </h3>
                <ArrowUpRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#FF4E00] transition-colors duration-300" />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00] mt-2">{f.role}</p>
              <p className="text-sm text-[#374151] leading-relaxed mt-3">{f.bio}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
